import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useCallback } from 'react'
import useSWRMutation from 'swr/mutation'

const API_V1_PREFIX = '/api/v1'
const RUN_LTA_GENERATION_PATH = `${API_V1_PREFIX}/lta-generation/run`

export interface ComputeLocalThinAreasRequest {
  cds_id: string
  data_frame_settings: string
  critical_threshold: number
  critical_threshold_unit: string
  indication_set: string
  create_flaw_boundaries: boolean
  create_critical_thickness_profiles: boolean
  create_rulers: boolean
}

export interface ComputeLocalThinAreasResponse {
  cdf_id: string
  indication_set: string
  lta_count: number
  entry_count: number
  entry_ids: string[]
  sandbox: boolean
}

function useComputeLocalThinAreasEndpoint() {
  const { FFS_SERVICE_URL } = useRuntimeVariables()

  if (!FFS_SERVICE_URL) {
    return null
  }

  return `${FFS_SERVICE_URL.replace(/\/$/, '')}${RUN_LTA_GENERATION_PATH}`
}

export function useComputeLocalThinAreas() {
  const { authorizedAxios } = useGeckoAuth()
  const endpoint = useComputeLocalThinAreasEndpoint()

  const { error, isMutating, trigger } = useSWRMutation<
    ComputeLocalThinAreasResponse,
    Error,
    string | null,
    ComputeLocalThinAreasRequest
  >(
    endpoint,
    async (url: string, { arg }: { arg: ComputeLocalThinAreasRequest }) => {
      const response =
        await authorizedAxios.post<ComputeLocalThinAreasResponse>(url, arg, {
          headers: { 'Content-Type': 'application/json' },
        })

      return response.data
    }
  )

  const computeLocalThinAreas = useCallback(
    async (request: ComputeLocalThinAreasRequest) => {
      if (!endpoint) {
        throw new Error('FFS service URL is not configured.')
      }

      return trigger(request)
    },
    [endpoint, trigger]
  )

  return {
    computeLocalThinAreas,
    isConfigured: endpoint !== null,
    isComputingLocalThinAreas: isMutating,
    error,
  }
}
